import styled from "styled-components";
import { tokens } from "../../../../public/tokens";
import { MOBILE_BREAKPOINT } from "../../../library/breakpoints";

// Doubled selector so it outranks Bootstrap's `.card` on the same element.
export const StyledAssessmentCard = styled.div({
  "&&": {
    padding: `${tokens.globals["spacing-3-5"]} ${tokens.globals["spacing-6"]} ${tokens.globals["spacing-6"]}`,
    background: tokens.colors.ui.card,
    border: `1px solid ${tokens.colors.ui.border}`,
    borderRadius: tokens.globals["border-radius"],
    boxShadow: tokens.effects.effects.shadow.default,
  },
});

/**
 * Section title row, with any trailing action pushed to the right.
 * @param {string} $gap - Space between the header and the section's fields.
 * @param {string} [$minHeight] - Row height when the title should centre against a button-height row.
 */
export const StyledSectionHeader = styled.div(({ $gap, $minHeight }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: tokens.globals["spacing-3"],
  minHeight: $minHeight,
  marginBottom: $gap,
}));

export const StyledSectionTitle = styled.div({
  ...tokens.typography.Heading["heading-5"],
  color: tokens.colors.text.primary,
});

export const StyledSectionDivider = styled.hr({
  margin: `${tokens.globals["spacing-5"]} 0`,
  borderTop: `1px solid ${tokens.colors.surfaces["border-divider"]}`,
});

/**
 * Equal-width field grid; collapses to one column on mobile.
 * @param {number} $columns - Desktop column count.
 */
export const StyledFieldGrid = styled.div(({ $columns }) => ({
  display: "grid",
  gridTemplateColumns: `repeat(${$columns}, minmax(0, 1fr))`,
  gap: tokens.globals["spacing-5"],
  alignItems: "start",

  [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
    gridTemplateColumns: "minmax(0, 1fr)",
  },
}));

// Groups within a section (e.g. Resources' editor, type cards, uploads) sit 30px apart.
export const StyledSectionStack = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: `calc(${tokens.globals["spacing-5"]} + ${tokens.globals["spacing-2-5"]})`,
});

/**
 * StyledFieldGrid cell spanning several columns; full width on mobile.
 * @param {number} $span - Columns to span.
 */
export const StyledGridSpan = styled.div(({ $span }) => ({
  gridColumn: `span ${$span}`,

  [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
    gridColumn: "auto",
  },
}));

/*
 * Grid cell that pins its controls to the input line of neighbouring labelled fields: offset by
 * the library inputs' 20px label + 1px gap, so error text under a neighbour can't shift it.
 */
export const StyledInputLineCell = styled.div({
  display: "flex",
  paddingTop: `calc(${tokens.globals["spacing-5"]} + ${tokens.globals["spacing-px"]})`,
});

/**
 * Input-height row of inline controls (buttons, checkboxes, toggles).
 * @param {string} $gap - Space between the controls.
 */
export const StyledInlineControls = styled.div(({ $gap }) => ({
  display: "flex",
  alignItems: "center",
  gap: $gap,
  minHeight: tokens.globals["spacing-10"],

  label: {
    marginBottom: 0,
  },
}));

export const StyledTimeLimitRow = styled.div({
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: tokens.globals["spacing-4"],
});

export const StyledTimeLimitLabel = styled.div({
  display: "flex",
  alignItems: "center",
  gap: tokens.globals["spacing-1"],
  fontFamily: tokens.typography.fontFamilies["albert-sans"],
  fontSize: tokens.typography.fontSize["lg-14px"],
  lineHeight: tokens.globals["spacing-5"],
  color: tokens.colors.text.primary,

  i: {
    color: tokens.colors.neutral.grey["700"],
  },

  "& > :last-child": {
    marginLeft: tokens.globals["spacing-2"],
  },
});

export const StyledTimeUnit = styled.div({
  display: "flex",
  alignItems: "center",
  gap: tokens.globals["spacing-2"],
  fontFamily: tokens.typography.fontFamilies["albert-sans"],
  fontSize: tokens.typography.fontSize["lg-14px"],
  fontWeight: tokens.typography.fontWeights.semibold,
  lineHeight: tokens.globals["spacing-5"],
  color: tokens.colors.text.primary,

  "& > :first-child": {
    width: "75px",
  },
});
